import { MergeAttributeClass } from "./sanitiseEmailHtml";

export interface LinkEdit {
  href: string;
  text: string;
  newTab: boolean;
}

export interface MergeAttribute {
  /** "1" is the audience's own entity, "2" the one the communication is regarding. */
  entity: string;
  attribute: string;
  attributeName: string;
  defaultValue: string;
  formatString: string;
}

const RegardingPrefix = "regarding_";

function closestIn(node: Node, root: HTMLElement, selector: string) {
  const element = node.nodeType === 1 ? (node as Element) : node.parentElement;
  const found = element?.closest(selector) as HTMLElement | null;

  return found && root.contains(found) && found !== root ? found : null;
}

function caretAfter(node: Node) {
  const range = document.createRange();

  range.setStartAfter(node);
  range.collapse(true);
  window.getSelection()?.removeAllRanges();
  window.getSelection()?.addRange(range);
}

export const linkAround = (root: HTMLElement, range: Range) =>
  closestIn(range.startContainer, root, "a") as HTMLAnchorElement | null;

/** The words selected, which a new link starts with as its text. */
export const selectedText = (range: Range) => range.toString();

/** Puts the link in place of the selection, or changes the one the caret is in. */
export function applyLink(root: HTMLElement, range: Range, link: LinkEdit) {
  const existing = linkAround(root, range);
  const anchor = existing ?? document.createElement("a");

  anchor.setAttribute("href", link.href);

  if (link.newTab) {
    anchor.setAttribute("target", "_blank");
  } else {
    anchor.removeAttribute("target");
  }

  anchor.textContent = link.text;

  if (!existing) {
    range.deleteContents();
    range.insertNode(anchor);
  }

  caretAfter(anchor);
}

/** Leaves the link's words, without the link. */
export function removeLink(root: HTMLElement, range: Range) {
  const anchor = linkAround(root, range);

  if (!anchor) {
    return false;
  }

  anchor.replaceWith(...Array.from(anchor.childNodes));
  return true;
}

export const mergeAttributeAround = (root: HTMLElement, range: Range) =>
  closestIn(range.startContainer, root, `span.${MergeAttributeClass}`);

/** What a merge attribute in the text holds, with a regarding attribute's prefix taken off. */
export function readMergeAttribute(element: HTMLElement): MergeAttribute {
  const stored = element.getAttribute("data-g4-attribute") ?? "";
  const regarding = stored.startsWith(RegardingPrefix);

  return {
    entity: element.getAttribute("data-g4-entity") || (regarding ? "2" : "1"),
    attribute: regarding ? stored.slice(RegardingPrefix.length) : stored,
    attributeName: element.textContent ?? "",
    defaultValue: element.getAttribute("data-g4-defaultvalue") ?? "",
    formatString: element.getAttribute("data-g4-formatstring") ?? "",
  };
}

/** Sets a merge attribute's data, as the email service reads it. */
function writeMergeAttribute(element: HTMLElement, value: MergeAttribute) {
  element.className = MergeAttributeClass;
  element.setAttribute(
    "data-g4-attribute",
    value.entity === "2" ? `${RegardingPrefix}${value.attribute}` : value.attribute
  );
  element.setAttribute("data-g4-entity", value.entity);
  element.setAttribute("data-g4-defaultvalue", value.defaultValue);
  element.setAttribute("data-g4-formatstring", value.formatString);
  element.setAttribute("contenteditable", "false");
  element.textContent = value.attributeName;
}

/** Changes `existing` in place, or puts a new merge attribute at the caret, spaced from its neighbours. */
export function applyMergeAttribute(
  range: Range,
  value: MergeAttribute,
  existing?: HTMLElement | null
) {
  if (existing) {
    writeMergeAttribute(existing, value);
    return;
  }

  const span = document.createElement("span");
  const fragment = document.createDocumentFragment();

  writeMergeAttribute(span, value);
  fragment.append(document.createTextNode(" "), span, document.createTextNode(" "));
  range.deleteContents();
  range.insertNode(fragment);
  caretAfter(span.nextSibling as Node);
}

/** Merge attributes are one piece in the editor; the saved text has no such marking. */
export function markMergeAttributes(root: HTMLElement) {
  root
    .querySelectorAll(`span.${MergeAttributeClass}`)
    .forEach((element) => element.setAttribute("contenteditable", "false"));
}
